import React from 'react';
import { Button, Card, Col, Row, Statistic, Typography, Table, Tag } from 'antd';
import {
    AppstoreOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    PushpinOutlined,
    ShopOutlined,
    ArrowRightOutlined,
    RiseOutlined,
    ThunderboltOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from 'recharts';
import moment from 'moment';
import PageTitle from '../../components/common/PageTitle';
import { branchOfficeMaster, itemModelMaster, salesInvoiceList } from '../../utility/mockData';

const { Text, Title } = Typography;

const Dashboard = () => {
    const navigate = useNavigate();

    // Calculate metrics dynamically
    const activeBranches = branchOfficeMaster.filter(b => b.status === 'Active').length;
    const totalSKUs = itemModelMaster.length;
    const totalInvoices = salesInvoiceList.length;
    const totalRevenue = salesInvoiceList.reduce((acc, curr) => acc + curr.amount, 0);

    // Brand counts for distribution
    const brandCounts = itemModelMaster.reduce((acc, curr) => {
        acc[curr.brand] = (acc[curr.brand] || 0) + 1;
        return acc;
    }, {});

    const brandChartData = Object.entries(brandCounts).map(([name, value]) => ({
        name,
        count: value
    }));

    // Sales chart data formatted chronologically (oldest to newest)
    const salesChartData = [...salesInvoiceList]
        .sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate))
        .map(inv => ({
            date: moment(inv.invoiceDate).format('DD MMM'),
            amount: inv.amount,
            customer: inv.customerName
        }));

    const recentInvoices = salesInvoiceList.slice(0, 3);
    const invoiceColumns = [
        {
            title: 'Invoice No',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
            render: (text) => <span style={{ fontWeight: 600, color: '#175AA0' }}>{text}</span>
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
        },
        {
            title: 'Branch Office',
            dataIndex: 'branchName',
            key: 'branchName',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (val) => <span style={{ fontWeight: 600 }}>₹{val.toLocaleString('en-IN')}</span>
        }
    ];

    const quickActions = [
        {
            title: 'Branch Office Master',
            description: 'Manage active retail branch offices, locations, and business head allocations.',
            icon: <ShopOutlined style={{ fontSize: '24px', color: '#175AA0' }} />,
            path: '/masters',
            bg: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
            border: '#bae7ff'
        },
        {
            title: 'SKU Item Catalog',
            description: 'Browse target technical specifications, pricing structures, and features.',
            icon: <AppstoreOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
            path: '/masters',
            bg: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
            border: '#d9f7be'
        },
        {
            title: 'Stock Lookup System',
            description: 'Check stock inventory availability levels dynamically across external branches.',
            icon: <FileSearchOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
            path: '/other-branch-stock',
            bg: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
            border: '#ffe7ba'
        },
        {
            title: 'Sales Invoices',
            description: 'Access official sales receipts, invoice transaction logs, and customer IMEI records.',
            icon: <FileDoneOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
            path: '/sales-invoice',
            bg: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
            border: '#efdbff'
        },
        {
            title: 'Push CRM Lead',
            description: 'Register customer leads, select nearby branch stores, and route leads directly.',
            icon: <PushpinOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
            path: '/push-lead',
            bg: 'linear-gradient(135deg, #fff0f6 0%, #ffd6e7 100%)',
            border: '#ffd6e7'
        }
    ];

    return (
        <PageTitle
            title="Operations Dashboard"
            subtitle="Real-time Sangeetha customer care system metrics & telemetry."
        >
            <style>{`
                .hover-card {
                    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
                }
                .hover-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 10px 20px rgba(23, 90, 160, 0.08) !important;
                    border-color: #175AA0 !important;
                }
                .action-card {
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .action-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
                }
                .recharts-default-tooltip {
                    border-radius: 8px !important;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                    border: 1px solid #e6edf7 !important;
                }
            `}</style>

            {/* KPI Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={24} sm={12} xl={6}>
                    <Card className="hover-card" style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Active Branch Stores</Text>}
                            value={activeBranches}
                            prefix={<ShopOutlined style={{ color: '#175AA0', marginRight: 8 }} />}
                            valueStyle={{ color: '#102a43', fontWeight: 700, fontSize: '26px' }}
                            suffix={<Tag color="success" style={{ marginLeft: 8, verticalAlign: 'middle', borderRadius: '4px' }}>LIVE</Tag>}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                    <Card className="hover-card" style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Catalog Models (SKUs)</Text>}
                            value={totalSKUs}
                            prefix={<AppstoreOutlined style={{ color: '#52c41a', marginRight: 8 }} />}
                            valueStyle={{ color: '#102a43', fontWeight: 700, fontSize: '26px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                    <Card className="hover-card" style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Registered Sales Invoices</Text>}
                            value={totalInvoices}
                            prefix={<FileDoneOutlined style={{ color: '#722ed1', marginRight: 8 }} />}
                            valueStyle={{ color: '#102a43', fontWeight: 700, fontSize: '26px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                    <Card className="hover-card" style={{ borderRadius: '16px', border: '1px solid #e6edf7', background: 'linear-gradient(135deg, #ffffff 0%, #f6f9fc 100%)' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Total Sales Volume</Text>}
                            value={totalRevenue}
                            prefix={<RiseOutlined style={{ color: '#ff7a00', marginRight: 8 }} />}
                            valueStyle={{ color: '#175AA0', fontWeight: 800, fontSize: '26px' }}
                            formatter={(val) => `₹${val.toLocaleString('en-IN')}`}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Recharts Analytics Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={24} lg={14}>
                    <Card 
                        title={<Title level={5} style={{ margin: 0, color: '#102a43' }}><RiseOutlined /> Revenue Analytics (Invoices)</Title>}
                        style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}
                    >
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={salesChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#175AA0" stopOpacity={0.4}/>
                                        <stop offset="95%" stopColor="#175AA0" stopOpacity={0.0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="date" stroke="#8c8c8c" fontSize={11} tickLine={false} />
                                <YAxis stroke="#8c8c8c" fontSize={11} tickLine={false} axisLine={false} formatter={(val) => `₹${val/1000}k`} />
                                <Tooltip formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']} />
                                <Area type="monotone" dataKey="amount" stroke="#175AA0" strokeWidth={3} fillOpacity={1} fill="url(#colorAmount)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card 
                        title={<Title level={5} style={{ margin: 0, color: '#102a43' }}><BarChartOutlined /> SKU Catalog by Brand</Title>}
                        style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}
                    >
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={brandChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="name" stroke="#8c8c8c" fontSize={11} tickLine={false} />
                                <YAxis stroke="#8c8c8c" fontSize={11} tickLine={false} axisLine={false} />
                                <Tooltip formatter={(value) => [value, 'Models']} />
                                <Bar dataKey="count" fill="#ff7a00" radius={[4, 4, 0, 0]}>
                                    {brandChartData.map((entry, index) => {
                                        const colors = { Apple: '#102a43', Samsung: '#175AA0', Xiaomi: '#fa8c16', OnePlus: '#f5222d', Vivo: '#722ed1' };
                                        return <Cell key={`cell-${index}`} fill={colors[entry.name] || '#ff7a00'} />;
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            {/* Split Section: Invoices */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col span={24}>
                    <Card 
                        title={<Title level={5} style={{ margin: 0, color: '#102a43' }}><FileDoneOutlined /> Recent Invoices Log</Title>}
                        extra={<Button type="link" onClick={() => navigate('/sales-invoice')} style={{ padding: 0 }}>View All <ArrowRightOutlined /></Button>}
                        style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}
                    >
                        <Table
                            columns={invoiceColumns}
                            dataSource={recentInvoices}
                            rowKey="invoiceNo"
                            pagination={false}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Quick Actions Panel */}
            <Title level={4} style={{ color: '#102a43', marginTop: 24, marginBottom: 16 }}><ThunderboltOutlined /> Operations Center</Title>
            <Row gutter={[16, 16]}>
                {quickActions.map((action) => (
                    <Col xs={24} md={12} xl={8} key={action.title}>
                        <Card
                            className="action-card"
                            onClick={() => navigate(action.path)}
                            style={{ 
                                borderRadius: '16px', 
                                border: '1px solid #e6edf7', 
                                background: '#ffffff',
                                height: '100%'
                            }}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                                <div style={{ 
                                    padding: '12px', 
                                    borderRadius: '12px', 
                                    background: action.bg, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    border: `1px solid ${action.border}`
                                }}>
                                    {action.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Title level={5} style={{ margin: '0 0 6px 0', color: '#102a43' }}>{action.title}</Title>
                                    <Text type="secondary" style={{ fontSize: '13px', display: 'block', lineHeight: '1.4' }}>
                                        {action.description}
                                    </Text>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        </PageTitle>
    );
};

export default Dashboard;
